import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChoiceButton } from "@/components/ChoiceButton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, ChevronDown, ChevronRight, Loader2, Play, RefreshCw, Undo2 } from "lucide-react";

/* DATES AND RATINGS SAVED AS PART NUMBERS (his ruling, 23 Oct): "count first, logged, undo, I press go — always".
   The count writes nothing. Go runs only on the count he read. Go / Undo / a one-tap decision need the
   `fix_labelled_values` toggle (Admin only until he grants it) — without it those buttons are absent. */
const n = (x) => (x || 0).toLocaleString();

const Num = ({ testid, big, label, sub }) => (
  <div data-testid={testid} className="rounded-xl border-2 border-slate-200 bg-white p-3">
    <div className="font-display text-2xl font-black leading-none text-[#1F1F1F] tabular-nums">{n(big)}</div>
    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1.5">{label}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

function Fold({ testid, title, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-bold text-[#1F1F1F] hover:bg-slate-50">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{title}
        <span className="ml-auto font-mono text-xs text-slate-500">{n(count)}</span>
      </button>
      {open && <div data-testid={testid} className="border-t border-slate-100 divide-y divide-slate-100 max-h-80 overflow-y-auto">{children}</div>}
    </div>
  );
}

function LeftAloneRow({ h, i, canFix, busy, decide }) {
  return (
    <div data-testid={`lv-left-${i}`} className="px-3 py-2 text-[13px] flex flex-wrap items-center gap-2">
      <span className="font-mono font-bold bg-slate-50 border border-slate-200 rounded px-1.5">{h.value}</span>
      <span className="text-slate-500">looks a bit like a {h.looks_like === "date_code" ? "date" : "rating"} · on {n(h.parts)} part{h.parts === 1 ? "" : "s"}</span>
      {canFix && (
        <span className="ml-auto flex flex-wrap gap-1.5">
          <ChoiceButton data-testid={`lv-left-date-${i}`} disabled={busy} onClick={() => decide(h.value, "date_code")}>Date code</ChoiceButton>
          <ChoiceButton data-testid={`lv-left-rating-${i}`} disabled={busy} onClick={() => decide(h.value, "rating")}>Rating</ChoiceButton>
          <ChoiceButton tone="sky" data-testid={`lv-left-keep-${i}`} disabled={busy} onClick={() => decide(h.value, "part_number")}>It's a part number, keep it</ChoiceButton>
        </span>
      )}
    </div>
  );
}

function LastRun({ run, canFix, busy, onUndo }) {
  const undone = run.status === "undone";
  return (
    <div data-testid="lv-last-run" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          Last run ({run.why}): <b data-testid="lv-last-run-moved">{n(run.moved)}</b> value(s) on <b>{n(run.parts)}</b> part(s) by {run.by} · {new Date(run.at).toLocaleString()}.
          {run.status !== "done" && <span data-testid="lv-last-run-undone"> {undone ? "Undone" : "Partly undone"} by {run.undone_by} — {n(run.undo_put_back)} put back{run.undo_left_alone?.length ? `, ${run.undo_left_alone.length} left alone (changed by a person since)` : ""}.</span>}
        </span>
        {canFix && !undone && (
          <ChoiceButton tone="danger" data-testid="lv-undo-run" disabled={busy} onClick={() => onUndo(null)} className="ml-auto">
            <Undo2 className="w-3.5 h-3.5 mr-1" />Undo this run
          </ChoiceButton>
        )}
      </div>
      <Fold testid="lv-run-parts" title={run.parts > (run.part_list?.length || 0) ? `Each part in this run (showing the first ${n(run.part_list.length)} of ${n(run.parts)}; Undo this run covers all of them)` : "Each part in this run"} count={run.parts}>
        {(run.part_list || []).map((p, i) => (
          <div key={p.part_id} data-testid={`lv-run-part-${i}`} className="px-3 py-2 text-[13px] flex flex-wrap items-center gap-2">
            <a href={`/parts/${p.part_id}`} className="font-mono font-bold text-[#B01E28]">{p.short_code}</a>
            <span className="text-slate-600">{p.name}</span>
            {p.values.map((v, j) => <span key={j} className="font-mono bg-white border border-slate-200 rounded px-1.5">{v.value} → {v.to}</span>)}
            {p.undone ? <span className="ml-auto text-xs text-slate-500">put back</span> : canFix && !undone && (
              <ChoiceButton tone="danger" data-testid={`lv-undo-part-${i}`} disabled={busy} onClick={() => onUndo(p)} className="ml-auto h-8 min-h-8">
                <Undo2 className="w-3 h-3 mr-1" />Undo this part
              </ChoiceButton>
            )}
          </div>
        ))}
      </Fold>
    </div>
  );
}

function Counts({ c }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Num testid="lv-would-move" big={c.would_move} label="Values Go would move" sub={`${n(c.dates)} dates · ${n(c.ratings)} ratings`} />
      <Num testid="lv-parts" big={c.parts} label="On this many parts" />
      <Num testid="lv-red-cleared" big={c.red_from_look_alike} label="Look-alike reds cleared" sub="the AI's own poor-read reds stay" />
      <Num testid="lv-unsure-kept" big={c.unsure_kept_searchable?.values} label="“A number printed on it” — kept searchable"
        sub={`on ${n(c.unsure_kept_searchable?.parts)} part(s) · never moved`} />
    </div>
  );
}

export function LabelledValuesCard() {
  const { has } = useAuth();
  const canFix = has("fix_labelled_values");
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);      // "go" | {undo: part|null}
  const box = useRef(null);
  const [seen, setSeen] = useState(false);   // the count runs when the card scrolls into view, not with the whole page
  const load = useCallback(() => {
    api.get("/catch-up/labelled-values/count").then(({ data }) => setC(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't count"));
  }, []);
  useEffect(() => {
    const el = box.current;
    if (!el || seen) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  useEffect(() => { if (seen) load(); }, [seen, load]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(ok(data)); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false); setAsk(null); load();
  };
  const go = () => run(() => api.post("/catch-up/labelled-values/go", { fingerprint: c.fingerprint }), (d) => `Moved ${n(d.run.moved)} value(s) on ${n(d.run.parts)} part(s) — Undo is beside the run`);
  const undo = (p) => run(() => api.post(`/catch-up/labelled-values/runs/${c.last_run.id}/undo`, null, { params: p ? { part_id: p.part_id } : {} }), (d) => `${n(d.put_back)} put back exactly as it was${d.left_alone?.length ? `, ${d.left_alone.length} left alone` : ""}`);
  const decide = (value, decision) => run(() => api.post("/catch-up/labelled-values/decide", { value, decision }),
    () => (decision === "part_number" ? `${value} stays a part number` : decision === "ask_again" ? `${value} is back on the list` : `${value} moved — Undo is beside the run`));

  return (
    <section ref={box} data-testid="labelled-values-card" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="px-4 py-3 border-b-2 border-[#1F1F1F] flex items-center gap-2 flex-wrap">
        <CalendarClock className="w-5 h-5 text-[#B01E28]" />
        <h2 className="font-display font-bold text-lg text-[#1F1F1F]">Dates and ratings saved as part numbers</h2>
        <ChoiceButton data-testid="lv-recount" onClick={load} className="ml-auto"><RefreshCw className="w-3.5 h-3.5 mr-1" />Count again</ChoiceButton>
      </div>
      <div className="p-4 space-y-3 text-sm text-slate-700">
        <p>A date (<span className="font-mono">11/30/18</span>) or a rating (<span className="font-mono">12 VOLT</span>) is a <b>labelled value</b>, not a part number. Go makes each one a <b>date code</b> or a <b>rating</b>: still found by searching its text, never scored as a part number, never fed to the red rule. Only a real month and day, or a number with a real unit, moves. The OEM # and Serial # boxes are never touched.</p>
        {!c ? <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Counting — nothing is written…</div> : (
          <>
            <p data-testid="lv-nothing-written" className="text-[12px] text-emerald-800 font-semibold">This count wrote nothing · counted in {n(c.counted_in_ms)} ms</p>
            <Counts c={c} />
            <Fold testid="lv-values" title="Every value Go would move" count={c.values.length}>
              {c.values.map((v, i) => (
                <div key={i} data-testid={`lv-value-${i}`} className="px-3 py-2 text-[13px] flex items-center gap-2">
                  <span className="font-mono font-bold">{v.value}</span><span className="text-slate-500">→ {v.to === "date_code" ? "date code" : "rating"} · {n(v.parts)} part(s)</span>
                </div>
              ))}
            </Fold>
            <Fold testid="lv-left-alone" title={c.left_alone_total > c.left_alone.length ? `Left alone — check by hand (showing the first ${n(c.left_alone.length)} of ${n(c.left_alone_total)})` : "Left alone — check by hand (half-matches like 20-6572)"} count={c.left_alone_total}>
              {c.left_alone.map((h, i) => <LeftAloneRow key={h.value} h={h} i={i} canFix={canFix} busy={busy} decide={decide} />)}
            </Fold>
            {c.kept_as_part_numbers.length > 0 && (
              <Fold testid="lv-kept" title="You said these are part numbers" count={c.kept_as_part_numbers.length}>
                {c.kept_as_part_numbers.map((k, i) => (
                  <div key={i} data-testid={`lv-kept-${i}`} className="px-3 py-2 text-[13px] flex items-center gap-2">
                    <span className="font-mono font-bold">{k.value}</span><span className="text-slate-500">by {k.by}</span>
                    {canFix && <ChoiceButton data-testid={`lv-kept-again-${i}`} disabled={busy} onClick={() => decide(k.value, "ask_again")} className="ml-auto h-8 min-h-8">Ask me again</ChoiceButton>}
                  </div>
                ))}
              </Fold>
            )}
            {canFix ? (
              <Button data-testid="lv-go" disabled={busy || !c.would_move} onClick={() => setAsk("go")}
                className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold"><Play className="w-4 h-4 mr-1.5" />Go — move these {n(c.would_move)}</Button>
            ) : (
              <p data-testid="lv-no-permission" className="text-[12px] text-slate-500">Go, Undo and the one-tap decisions need the “Fix dates and ratings saved as part numbers” toggle. Only Admin has it unless it's granted on the Permissions screen.</p>
            )}
            {c.last_run && <LastRun run={c.last_run} canFix={canFix} busy={busy} onUndo={(p) => setAsk({ undo: p })} />}
          </>
        )}
      </div>
      <Dialog open={!!ask} onOpenChange={(v) => !v && setAsk(null)}>
        <DialogContent data-testid="lv-confirm" className="rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-[#B01E28]">{ask === "go" ? `Move ${n(c?.would_move)} value(s) to labelled values?` : ask?.undo ? `Undo ${ask.undo.short_code}?` : "Undo the whole run?"}</DialogTitle>
            <DialogDescription>{ask === "go"
              ? "This one writes. It runs only if the count is still the one on screen. Every part gets a line in its history, and Undo sits beside the run and beside each part."
              : "Every number goes back exactly as it was, except one a person has changed since — that one is left alone and listed."}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="lv-confirm-cancel" onClick={() => setAsk(null)}>Not yet</Button>
            <Button data-testid="lv-confirm-yes" disabled={busy} onClick={() => (ask === "go" ? go() : undo(ask.undo))} className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : ask === "go" ? "Yes — go" : "Yes — undo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
